import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  UsersIcon,
  CurrencyDollarIcon,
  CommandLineIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import DashboardStats from "../components/DashboardStats";
import PortfolioDistribution from "../components/PortfolioDistribution";
import ExchangeConnections from "../components/ExchangeConnections";
import BotDistribution from "../components/BotDistribution";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { translations } from "../utils/translations";
import useAuth from "../hooks/useAuth";

// Sample data for demonstration
const initialStats = [
  {
    id: 1,
    name: "Total Users",
    value: "1,248",
    icon: UsersIcon,
    change: "+8.2%",
    changeType: "increase",
  },
  {
    id: 2,
    name: "Total Transactions",
    value: "24,843",
    icon: DocumentTextIcon,
    change: "+12.5%",
    changeType: "increase",
  },
  {
    id: 3,
    name: "Active Bots",
    value: "325",
    icon: CommandLineIcon,
    change: "+5.7%",
    changeType: "increase",
  },
  {
    id: 4,
    name: "Total Subscriptions",
    value: "$78,621",
    icon: CurrencyDollarIcon,
    change: "+10.3%",
    changeType: "increase",
  },
];

export default function Dashboard() {

  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  const [stats, setStats] = useState(initialStats);
  // const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState("en"); // Default to English
  const t = translations[language]; // Get translations for current language


  useEffect(() => {
    navigate("/admin-v2/users")
  }, [])
  
  // Update stats with translated labels
  useEffect(() => {
    const updatedStats = [
      {
        id: 1,
        name: t.totalUsers,
        value: "1,248",
        icon: UsersIcon,
        change: "+8.2%",
        changeType: "increase",
      },
      {
        id: 2,
        name: t.totalOrders,
        value: "24,843",
        icon: DocumentTextIcon,
        change: "+12.5%",
        changeType: "increase",
      },
      {
        id: 3,
        name: t.activeBots,
        value: "325",
        icon: CommandLineIcon,
        change: "+5.7%",
        changeType: "increase",
      },
      {
        id: 4,
        name: t.totalSubscriptions,
        value: "$78,621",
        icon: CurrencyDollarIcon,
        change: "+10.3%",
        changeType: "increase",
      },
    ];
    setStats(updatedStats);
  }, [language, t]);

  const [botDistributionData, setBotDistributionData] = useState([
    { strategy: "DCA", active: 86, inactive: 14 },
    { strategy: "GRID", active: 124, inactive: 22 },
    { strategy: "GRID_V2", active: 65, inactive: 8 },
    { strategy: "GRID_V3", active: 42, inactive: 12 },
    { strategy: "REBALANCE", active: 8, inactive: 3 },
  ]);

  const [portfolioData, setPortfolioData] = useState([
    { name: "Bitcoin (BTC)", value: 38.5 },
    { name: "Ethereum (ETH)", value: 24.2 },
    { name: "BNB", value: 8.7 },
    { name: "XRP", value: 5.8 },
    { name: "Solana (SOL)", value: 7.3 },
    { name: "Cardano (ADA)", value: 3.6 },
    { name: "Dogecoin (DOGE)", value: 2.4 },
    { name: "Others", value: 9.5 },
  ]);

  // Adapt exchange connection data for the selected language
  const getLocalizedConnections = () => {
    const baseConnections = [
      {
        name: "Binance",
        status: "connected",
        lastChecked: language === "en" ? "1 minute ago" : "1分前",
      },
      {
        name: "Coinbase",
        status: "connected",
        lastChecked: language === "en" ? "5 minutes ago" : "5分前",
      },
      {
        name: "Kraken",
        status: "connected",
        lastChecked: language === "en" ? "3 minutes ago" : "3分前",
      },
      {
        name: "Huobi",
        status: "limited",
        lastChecked: language === "en" ? "10 minutes ago" : "10分前",
      },
      {
        name: "Kucoin",
        status: "disconnected",
        lastChecked: language === "en" ? "30 minutes ago" : "30分前",
      },
      {
        name: "OKX",
        status: "connected",
        lastChecked: language === "en" ? "7 minutes ago" : "7分前",
      },
    ];
    return baseConnections;
  };

  const [exchangeConnections, setExchangeConnections] = useState(
    getLocalizedConnections(),
  );

  // Update connections when language changes
  useEffect(() => {
    setExchangeConnections(getLocalizedConnections());
  }, [language]);

  const [totalPortfolio, setTotalPortfolio] = useState({
    totalValue: "$3,851,267",
    btcValue: "94.28 BTC",
    change24h: "+2.3%",
  });

  // Function to refresh data
  const refreshData = async () => {
    // setLoading(true);
    // try {
    //   // In a real app, these would be actual API calls
    //   // const statsResponse = await axios.get('/api/admin/dashboard/stats');
    //   // const botDistributionResponse = await axios.get('/api/admin/dashboard/bot-distribution');
    //   // const portfolioResponse = await axios.get('/api/admin/dashboard/portfolio');
    //   // const exchangeResponse = await axios.get('/api/admin/dashboard/exchanges');

    //   // For now, we'll just simulate a loading delay
    //   await new Promise((resolve) => setTimeout(resolve, 1000));

    //   // Update stats with translated labels
    //   const updatedStats = [
    //     {
    //       id: 1,
    //       name: t.totalUsers,
    //       value: "1,248",
    //       icon: UsersIcon,
    //       change: "+8.2%",
    //       changeType: "increase",
    //     },
    //     {
    //       id: 2,
    //       name: t.totalOrders,
    //       value: "24,843",
    //       icon: DocumentTextIcon,
    //       change: "+12.5%",
    //       changeType: "increase",
    //     },
    //     {
    //       id: 3,
    //       name: t.activeBots,
    //       value: "325",
    //       icon: CommandLineIcon,
    //       change: "+5.7%",
    //       changeType: "increase",
    //     },
    //     {
    //       id: 4,
    //       name: t.totalSubscriptions,
    //       value: "$78,621",
    //       icon: CurrencyDollarIcon,
    //       change: "+10.3%",
    //       changeType: "increase",
    //     },
    //   ];
    //   setStats(updatedStats);

    //   // Update exchange connections with localized time
    //   setExchangeConnections(getLocalizedConnections());
    // } catch (error) {
    //   console.error("Error refreshing dashboard data:", error);
    // } finally {
    //   setLoading(false);
    // }
  };

  // Refresh data on component mount or language change
  useEffect(() => {
    refreshData();
    // Set up interval to refresh data every 5 minutes
    const interval = setInterval(refreshData, 300000);
    return () => clearInterval(interval);
  }, [language]);

  // Recent subscription data adapted for language selection
  const getRecentSubscriptions = () => {
    const baseSubscriptions = [
      {
        uid: "U-78945",
        name: "John Smith",
        email: "john.smith@example.com",
        package: "Professional",
        price: "$149.99",
      },
      {
        uid: "U-78921",
        name: "Emma Johnson",
        email: "emma.j@example.com",
        package: "Starter",
        price: "$49.99",
      },
      {
        uid: "U-78903",
        name: "Michael Brown",
        email: "michael.b@example.com",
        package: "Enterprise",
        price: "$299.99",
      },
      {
        uid: "U-78895",
        name: "Sophia Davis",
        email: "sophia.d@example.com",
        package: "Professional",
        price: "$149.99",
      },
      {
        uid: "U-78880",
        name: "Robert Wilson",
        email: "robert.w@example.com",
        package: "Starter",
        price: "$49.99",
      },
    ];

    return baseSubscriptions.map((sub, index) => ({
      ...sub,
      time:
        language === "en"
          ? index === 0
            ? "10 minutes ago"
            : index === 1
            ? "25 minutes ago"
            : index === 2
            ? "45 minutes ago"
            : index === 3
            ? "1 hour ago"
            : "2 hours ago"
          : index === 0
          ? "10分前"
          : index === 1
          ? "25分前"
          : index === 2
          ? "45分前"
          : index === 3
          ? "1時間前"
          : "2時間前",
    }));
  };

  if(!isAuthenticated) return <></>

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t.dashboard}</h2>
          <p className="mt-1 text-sm text-gray-500">{t.overview}</p>
        </div>

        <div className="flex space-x-3 items-center">
          <LanguageSwitcher language={language} setLanguage={setLanguage} />

          <button
            onClick={refreshData}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md flex items-center"
          >
            <ArrowPathIcon
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            {loading ? t.refreshing : t.refreshDashboard}
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <DashboardStats stats={stats} />

      {/* Portfolio Summary */}
      <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-100">
        <div className="p-5 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {t.overallPortfolio}
          </h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-500">
                {t.totalPortfolioValue}
              </div>
              <div className="text-2xl font-semibold text-gray-900">
                {totalPortfolio.totalValue}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-500">{t.change24h}</div>
              <div className="text-2xl font-semibold text-green-600">
                {totalPortfolio.change24h}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Bot Distribution by Strategy */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-100">
          <div className="p-5 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t.botDistribution}
            </h3>
          </div>
          <div className="p-5">
            <BotDistribution botData={botDistributionData} />
          </div>
        </div>

        {/* Portfolio Distribution */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-100">
          <div className="p-5 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t.portfolioDistribution}
            </h3>
          </div>
          <div className="p-5">
            <PortfolioDistribution portfolioData={portfolioData} />
          </div>
        </div>
      </div>

      {/* Exchange API Status */}
      <ExchangeConnections
        connections={exchangeConnections}
        onRefresh={refreshData}
      />

      {/* Recent Subscriptions */}
      <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-100">
        <div className="px-5 py-5 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {language === "en"
              ? "Recent Subscriptions"
              : "Recent Subscriptions"}
          </h3>
          <Link
            to="/admin-v2/subscriptions"
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md flex items-center text-sm"
          >
            See More
          </Link>
        </div>
        <div className="p-5">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    UID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Package
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Price
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getRecentSubscriptions().map((sub, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {sub.uid}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {sub.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sub.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          sub.package === "Enterprise"
                            ? "bg-purple-100 text-purple-800"
                            : sub.package === "Professional"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {sub.package}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {sub.price}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sub.time}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
